import axios from 'axios';

if (!customElements.get('custom-quiz')) {
    class Quiz extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
          this.setupQuizData();
          this.setupEventListeners();
        }

        setupQuizData() {
          this.data = {
            primaryOffer: {},
            secondaryOffer: {},
            userInformation: {}
          }

          this.totalQuestions = Number(this.dataset.totalQuestions) || 0;
          this.currentStep = Number(this.dataset.currentStep) || 0;
          this.nextButtons = this.querySelectorAll('.quiz-next-button');
          this.backButton = this.querySelector('.quiz-menu__back');
          this.progressEl = this.querySelector('.progress');
          this.inputAnswers = [...this.querySelectorAll('.quiz-answer--input, .quiz-answer--select')];
          this.ifSelectedExcludeQuestions = this.querySelectorAll('[data-if-selected-exclude-questions]');
          this.summaryTemplate = this.querySelector('#quiz-result__customer-summary-fit');
          this.headingTemplate = this.querySelector('#quiz-result__heading');
          this.sliderInputs = this.querySelectorAll('.quiz-answer--slider input');
          this.allQuestions = this.querySelectorAll('.quiz-question[data-metafield-handle]');
          this.questionsWithTextAnswer = this.querySelectorAll('.quiz-question:has(.quiz-answer--text)');
          this.resultStep = this.querySelector('.quiz-result');
        }

        setupEventListeners() {
          this.handlePrevStep = this.handlePrevStep.bind(this);
          this.handleNextStep = this.handleNextStep.bind(this);
          this.selectTextAnswer = this.selectTextAnswer.bind(this);
          this.handleSliderInput = this.handleSliderInput.bind(this);
          this.toggleQuestion = this.toggleQuestion.bind(this);
          this.validateAnswers = this.validateAnswers.bind(this);
          
          this.backButton?.addEventListener('click', this.handlePrevStep);
          this.nextButtons.forEach(button => button.addEventListener('click', this.handleNextStep));
          this.inputAnswers.forEach(answer => answer.addEventListener('change', this.validateAnswers));
          this.questionsWithTextAnswer.forEach(question => question.addEventListener('click', (e) => this.selectTextAnswer(question, e)));

          this.sliderInputs.forEach(input => {
            input.addEventListener('input', () => this.handleSliderInput(input));

            const nextButton = input.closest('.quiz-question-wrapper')?.querySelector('.quiz-next-button--disable');
            if (nextButton) nextButton.classList.remove('quiz-next-button--disable');
          });

          this.ifSelectedExcludeQuestions.forEach(question => {
            question.addEventListener('click', () => this.toggleQuestion(question));
            question.addEventListener('quiz:answer-excludes-question-updated', () => this.toggleQuestion(question));
          });
        }

        toggleQuestion(answer) {
          const questionNames = answer.dataset.ifSelectedExcludeQuestions.split(',')?.filter(Boolean);

          function handleQuestion() {
            questionNames.forEach(questionName => {
              const question = this.querySelector(`[data-question-name="${questionName}"]`);
              if (answer.classList.contains('selected-answer')) {
                question?.classList.add('excluded-question');
              } else {
                question?.classList.remove('excluded-question');
              }
            })
          }

          // allowing classes to set in before executing
          setTimeout(handleQuestion.bind(this));
        }

        enableNextButton(button) {
          button?.classList.remove('quiz-next-button--disable');
        }

        disableNextButton(button) {
          button?.classList.add('quiz-next-button--disable');
        }

        validateInputAnswers(inputAnswers, currentButton) {
          const allFilled = inputAnswers.every(inputAnswer => {
            const input = inputAnswer.querySelector('.quiz-answer__content');
            if (!input) return false;
            
            const inputType = input.type;
            const inputValue = input.value.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            
            if (inputType === 'email') {
              if (!emailRegex.test(inputValue)) return false;
              return true;
            }
            
            if (inputType !== 'number' && inputValue?.length < 3) return false;
            if (inputValue === '') return false;

            return true;
          });

          if (allFilled) this.enableNextButton(currentButton);
          else this.disableNextButton(currentButton);
        }

        validateAnswers() {
          const currentButton = this.nextButtons[this.currentStep + 1];

          if (this.currentStep === 0) {
            this.enableNextButton(currentButton);
            return;
          };

          const currentStepEl = this.querySelector(`.quiz-step[data-step-no="${this.currentStep}"]`);
          const inputAnswers = [...currentStepEl?.querySelectorAll('.quiz-answer--input, .quiz-answer--select')];

          if (inputAnswers.length) {
            // Requires all answers to be filled
            this.validateInputAnswers(inputAnswers, currentButton);
            return;
          }

          const answers = currentStepEl?.querySelectorAll('.quiz-answer.selected-answer');
          
          if (answers.length > 0) {
            this.enableNextButton(currentButton);
            return;
          }

          this.disableNextButton(currentButton);
        }

        handleNextStep() {
          this.currentStep++;
          this.switchStep('next');
        }

        handlePrevStep() {
          this.currentStep--;
          this.switchStep('prev');
        }

        switchStep(direction) {
          const currentStepEl = this.querySelector('.quiz-step.current-step');
          const nextStepEl = this.querySelector(`.quiz-step[data-step-no="${this.currentStep}"]`);

          // Skip loading animation when returning from results
          if (
            direction == 'prev' 
            && nextStepEl?.classList.contains('quiz-transition')
          ) {
            this.handlePrevStep();
            return;
          }

          // Skip excluded questions
          if (nextStepEl?.classList.contains('excluded-question')) {
            if (direction == 'prev') this.handlePrevStep();
            else this.handleNextStep();
            return;
          }

          // Return to homepage when click on back button on intro step
          if (this.currentStep < 0) window.location.href = "/";
          if (!nextStepEl && (this.currentStep >= this.totalQuestions)) return;

          currentStepEl?.classList.remove('current-step');
          nextStepEl?.classList.add('current-step');
          this.setAttribute('data-current-step', this.currentStep);

          this.updateProgress();

          if (this.currentStep == (this.totalQuestions + 1)) this.handleResult();
        }

        updateProgress() {
          if (!this.progressEl) return;

          let progress = Math.trunc(((this.currentStep - 1) / this.totalQuestions) * 100);
          if (progress > 100) progress = 100;
          let progressLabel = '‎ ';
          if (progress > 0) progressLabel = `'${progress}%'`;

          if (progress > 100) this.progressEl.classList.add('progress--max');
          else this.progressEl.classList.remove('progress--max');

          this.progressEl.style.setProperty('--progress', `${progress}%`);
          this.progressEl.style.setProperty('--progress-text', progressLabel);
        }

        handleSliderInput(input) {
          const sliderValueEl = input.closest('.quiz-answer--slider')?.querySelector('.quiz-answer--slider-value');
          if (sliderValueEl) sliderValueEl.textContent = input.value;
        }

        selectTextAnswer(question, e) {
          const clickedAnswer = e?.target.classList.contains('quiz-answer') ? e.target : e?.target.closest('.quiz-answer');
          if (!clickedAnswer) return;

          const answerAlreadySelected = clickedAnswer.classList.contains('selected-answer');
          if (answerAlreadySelected) {
            clickedAnswer.classList.remove('selected-answer');
            this.validateAnswers();
            return;
          }

          const allAnswers = question.querySelectorAll('.quiz-answer.quiz-answer--text');
          allAnswers.forEach(answer => {
            if (answer.classList.contains('selected-answer')) {
              answer.classList.remove('selected-answer');
              
              if (!answer.dataset.ifSelectedExcludeQuestions) return;
              const event = new CustomEvent('quiz:answer-excludes-question-updated', {
                detail: {
                  answer: answer,
                }
              });
              answer.dispatchEvent(event);
            }
          });

          clickedAnswer.classList.add('selected-answer');

          const answerInput = clickedAnswer.querySelector('input');
          if (answerInput && question?.dataset?.metafieldHandle) 
            answerInput.setAttribute('data-metafield-handle', question.dataset.metafieldHandle);

          this.validateAnswers();
        }

        saveProductsToLocalStorage(products, collectionHandle) {
          try {
            localStorage.setItem(collectionHandle, JSON.stringify(products));
          } catch (e) {
            console.error('Failed to save products to localStorage:', e);
          }
        };

        loadProductsFromLocalStorage(collectionHandle) {
          try {
            return JSON.parse(localStorage.getItem(collectionHandle));
          } catch (e) {
            console.error('Failed to load products from localStorage:', e);
            return null;
          }
        };

        async loadProducts(collectionHandle) {
          // loadProductsFromLocalStorage is currently disabled
          
          const products = await this.fetchAllProductsInCollection(collectionHandle);
          this.saveProductsToLocalStorage(products, collectionHandle);
        
          return products;
        };

        async handleResult() {
          this.getUsersInformation();

          const collectionHandle = this.resultStep.dataset.collectionHandle;
          if (!collectionHandle) {
            console.error('No collection handle found');
            return;
          }

          const products = await this.loadProducts(collectionHandle) || [];

          const matchingProducts = this.getMatchingProducts(products);
          if (matchingProducts.length) await this.buildResultUI(matchingProducts, false);

          if (!matchingProducts.length) {
            // Fallback filters Snefrid products, based on users set budget

            // WARNING: Not handled for when no snefreid matches are found, currently assuming there will always be a match within the budget.
            // WARNING: Sefried is hardcoded inside getFallbackMatches.

            const matchingFallback = this.getFallbackMatches(products);
            if (matchingFallback.length) await this.buildResultUI(matchingFallback, true);
          }
          
          // When result page is ready, switch to it
          if (matchingProducts.length || matchingFallback.length) this.handleNextStep();
          else console.error('No matching products found');
        }

        getFallbackMatches(products) {
          return products.filter(product => {
            const isSenrid = product.title.toLowerCase().includes('snefrid');
            return isSenrid;
          }).map(product => {
            const variants = product.variants.edges.map(variant => {
              const node = variant.node;
              const isWithinBudget = this.getIsWithinBudget(node);
              return isWithinBudget ? variant : null;
            }).filter(Boolean).sort((a, b) => {
              // sorting variants by price
              const aPrice = parseFloat(+a.node.price.amount);
              const bPrice = parseFloat(+b.node.price.amount);
              return bPrice - aPrice;
            });

            if (!variants.length) return null;

            return {
              ...product,
              variants: {
                edges: variants
              }
            }
          }).filter(Boolean).sort((a, b) => {
            // sorting products by price
            const aPrice = parseFloat(+a.variants.edges[0].node.price.amount);
            const bPrice = parseFloat(+b.variants.edges[0].node.price.amount);
            return bPrice - aPrice;
          });
        }

        async buildOffer(productHandle, variantId, targetSelector, offerTypeSuffix) {
          const templateSuffix = 'quiz-result-offer';
          const fetchUrl = `/products/${productHandle}?view=${templateSuffix}&variant=${variantId}&${offerTypeSuffix}`;

          try {
            const response = await fetch(fetchUrl);
            if (!response.ok) {
                throw new Error(`Network response was not ok. Status: ${response.status}`);
            }
    
            const htmlText = await response.text();
            const parser = new DOMParser();
            const htmlDocument = parser.parseFromString(htmlText, 'text/html');
            const offerSectionEl = htmlDocument.querySelector('.offers_page_category');

            if (offerTypeSuffix.includes('secondary-offer')) 
              await this.addSecondaryHeading(offerSectionEl);

            this.setOfferSizeVariantFirst(offerSectionEl);

            const sectionHtml = offerSectionEl.innerHTML;
            const targetElement = this.querySelector(targetSelector);
    
            if (targetElement) {
                targetElement.innerHTML = sectionHtml;
                
                const offerInserted = new CustomEvent('quiz-offer-inserted');
                document.dispatchEvent(offerInserted);
            } else {
                console.error(`Error: Target element ${targetSelector} not found.`);
            }
          } catch (error) {
              console.error("Error fetching section:", error);
          }
        }

        async addSecondaryHeading(offerSectionEl) {
          const productCardWrappers = offerSectionEl.querySelectorAll('.campaign__product_card__wrapper');
          const headingEl = this.querySelector('[data-secondary-offer-heading]');
          const secondaryOfferWrapperEl = this.querySelector('.quiz-result__secondary-offer');
          if (!headingEl || !secondaryOfferWrapperEl || !productCardWrappers.length) return;
          
          const headingText = headingEl.dataset.secondaryOfferHeading;
          const secondaryHeadingEl = document.createElement('h2');
          secondaryHeadingEl.classList.add('quiz-result__secondary-offer-heading');
          secondaryHeadingEl.textContent = headingText;
          
          productCardWrappers.forEach(productCardWrapper => {
            const cloneHeading = secondaryHeadingEl.cloneNode(true);
            productCardWrapper.prepend(cloneHeading);
          });

          const cloneHeading = secondaryHeadingEl.cloneNode(true);
          offerSectionEl.prepend(cloneHeading);
          cloneHeading.classList.add('secondary-offer-heading-top');
        }

        setOfferSizeVariantFirst(sectionHtml) {
          const cardsContainerEl = sectionHtml.querySelector('.campaign_product');
          const primaryWrapperEl = cardsContainerEl.querySelector('.campaign__image_and_card_wrapper[data-set-first]');
          const primarySizeVariant = primaryWrapperEl.querySelector('.size-variants .variant.selected').textContent.trim();
          cardsContainerEl.insertBefore(primaryWrapperEl, cardsContainerEl.firstChild);

          cardsContainerEl.querySelectorAll('.size-variants').forEach(sizeVariantContainer => {
            const sizeVariantEls = sizeVariantContainer.querySelectorAll('.variant');
            sizeVariantEls.forEach(sizeVariantEl => {
              if (sizeVariantEl.textContent.includes(primarySizeVariant)) {
                sizeVariantContainer.insertBefore(sizeVariantEl, sizeVariantEls[0]);
              }
            })
          })
        }

        async buildPrimaryOffer(products) {
          try {
            await this.buildOffer(products[0].handle, products[0].variants.edges[0].node.id, '.quiz-result__primary-offer', 'quiz-result-primary-offer');

            this.data.primaryOffer.product = products[0];
            this.data.primaryOffer.variant = products[0].variants.edges[0].node;
          } catch (error) {
            console.error("Error fetching primary offer:", error);
          }
        }

        async buildSecondaryOffer(products) {
          let productHandle;
          let variantId;

          if (products[1]) {
            productHandle = products[1].handle;
            variantId = products[1].variants.edges[0].node.id;
          } else if (products[0] && products[0].variants.edges[1]) {
            productHandle = products[0].handle;
            variantId = products[0].variants.edges[1].node.id;
          } else {
            return;
          }

          await this.buildOffer(productHandle, variantId, '.quiz-result__secondary-offer', 'quiz-result-secondary-offer');
        }

        async buildResultUI(products, isUsingFallback) {
          this.updateHeading();
          await this.buildPrimaryOffer(products);
          await this.buildSecondaryOffer(products);
          if (!isUsingFallback) this.personalizeCustomerSummaryFit();
          this.getAboutSection();
        }

        personalizeCustomerSummaryFit() {
          if (!this.data?.primaryOffer?.product || !this.summaryTemplate) return;

          const summaryFitClonedEl = this.summaryTemplate.content.cloneNode(true);
          const customerSummaryFitEl = summaryFitClonedEl.querySelector('.quiz-result__customer-summary-fit');
          if (!customerSummaryFitEl) return;
        
          const replacements = {};
        
          const productTitle = this.data?.primaryOffer?.product?.title;
          if (productTitle && productTitle.includes('|')) {
            const [bedName, bedSize] = productTitle.split('|');
            if (bedName) replacements["[bed_name]"] = `<strong>${bedName.trim()}</strong>`;
            if (bedSize) replacements["[bed_size]"] = `<strong>${bedSize.trim()}</strong>`;
          }
        
          const height = this.data?.userInformation?.height;
          if (height) replacements["[user_height]"] = `<strong>${height}</strong>`;
        
          const weight = this.data?.userInformation?.weight;
          if (weight) replacements["[user_weight]"] = `<strong>${weight}</strong>`;
        
          customerSummaryFitEl.innerHTML = customerSummaryFitEl.innerHTML.replace(/\[.*?\]/g, match => {
            return replacements[match] ?? match;
          });

          customerSummaryFitEl.classList.remove('hidden');

          const currentSummary = this.querySelector('.quiz-result__customer-summary-fit');
          if (currentSummary) currentSummary.remove();

          const primaryOfferEl = this.querySelector('.quiz-result__primary-offer');
          primaryOfferEl.after(customerSummaryFitEl);
        }

        getAboutSection() {
          const aboutSection = document.querySelector('main > section[id*="__collage"]');
          if (aboutSection) {
            this.append(aboutSection);
          }
        }

        updateHeading() {
          if (!this.headingTemplate) return;

          const userName = this.data.userInformation.Name;
          const headingClonedTemplate = this.headingTemplate.content.cloneNode(true);
          const headingEl = headingClonedTemplate.querySelector('.quiz-result__heading');
          if (!headingEl) return;
          
          if (userName) headingEl.innerHTML = headingEl.innerHTML.replace('{#name}', userName);
          else headingEl.innerHTML = headingEl.innerHTML.replaceAll('{#name}', 'Hi');

          const currentHeading = this.querySelector('.quiz-result__heading');
          if (currentHeading) currentHeading.remove();

          this.headingTemplate.after(headingEl);
        }

        getMatchingProducts(products) {
          const selectedAnswersEls = this.querySelectorAll('.quiz-question:not(.excluded-question) .selected-answer input');

          // Preparing what metafiels and values should be looked for
          const selectedAnswers = Array.from(selectedAnswersEls).map(answer => {
            const isSlider = answer.classList.contains('quiz-answer__slider-input');
            const metafieldHandle = answer.dataset.metafieldHandle?.split('.');
            const [namespace, key] = Array.isArray(metafieldHandle) ? metafieldHandle : [];
            if (!key) return;

            // compatibleWith holds values that will be searched for within the metafield
            // normalizing the values so JSON can read them
            let compatibleWith = this.decodeHtmlEntities(answer.dataset.compatibleWith);
            if (!compatibleWith || compatibleWith == "undefined") return;

            compatibleWith = JSON.parse(compatibleWith);

            if (isSlider) {
              // Determining what value should be matched for slider answer
              compatibleWith = compatibleWith.reduce((acc, val) => {

                const slideLogicParts = val.split(/(==|<|>)/);

                if (slideLogicParts.length === 3) {
                  const logicBaseValue = slideLogicParts[0].trim().includes('-') ? slideLogicParts[0].trim().split('-') : +slideLogicParts[0].trim();
                  const logicOperator = slideLogicParts[1].trim();
                  const logicMatchValue = slideLogicParts[2].trim();
                  const selectedNumber = +answer.value;
                  if (logicOperator === '==') {
                    if (
                      // if range
                      Array.isArray(logicBaseValue) 
                      && selectedNumber > +logicBaseValue[0] 
                      && selectedNumber <= +logicBaseValue[1]
                      // if single value
                      || logicBaseValue == selectedNumber
                    ) return acc.concat(logicMatchValue);
                  } else if (logicOperator === '<') {
                    if (selectedNumber < logicBaseValue) return acc.concat(logicMatchValue);
                  } else if (logicOperator === '>') {
                    if (selectedNumber > logicBaseValue) return acc.concat(logicMatchValue);
                  }
                }

                return acc;
              }, []);
            }

            const formattedAnswer = {
              namespace: namespace.split(':')[1] ? namespace.split(':')[1] : namespace,
              key,
              compatibleValues: compatibleWith,
              searchVariant: namespace.split(':')[1] ? true : false
            }

            return formattedAnswer;
          })
          // Remove undefined answers before continuing
          .filter(Boolean)
          // Keep only bed_size height value for the taller person so everyone can fit 
          .reduce((arrayAnswers, currentAnswer) => {
            if (currentAnswer.namespace !== 'custom' && currentAnswer.key !== 'bed_size') return [...arrayAnswers, currentAnswer];
            const isOnlyHeightValue = currentAnswer.compatibleValues.every(val => /^x\d+$/.test(val));
            if (!isOnlyHeightValue) return [...arrayAnswers, currentAnswer];
        
            const existing = arrayAnswers.find(
              a => a.namespace === 'custom' && a.key === 'bed_size' && a.compatibleValues.every(v => /^x\d+$/.test(v))
            );
        
            if (!existing) return [...arrayAnswers, currentAnswer];
        
            const max = vals => Math.max(...vals.map(v => parseInt(v.slice(1))));
            return max(currentAnswer.compatibleValues) > max(existing.compatibleValues)
              ? arrayAnswers.map(a => (a === existing ? currentAnswer : a))
              : arrayAnswers;
          }, []);

          function filterMatchingProducts(products, selectedAnswers) {
            return products
              ?.map(product => {
                const productMetafields = product.metafields || [];
          
                // Check if the product matches all non-variant-related answers
                const isProductMatch = selectedAnswers?.every(answer => {
                  let metafield;
                  if (!answer.searchVariant) {
                    metafield = productMetafields.find(metafield =>
                      metafield?.namespace === answer.namespace && metafield?.key === answer.key
                    );
          
                    // Logic that accounts for them having a "false" value or not filled metafield
                    if (answer.compatibleValues?.length === 1 && answer.compatibleValues[0] === "false") {
                      return !metafield || metafield.value === "false";
                    }
          
                    // Product must have a metafield with a compatible value
                    return metafield && answer.compatibleValues.some(value => {
                      // strict match so 80x200 doesn't match 180x200
                      const twoDigitPattern = /^\d{2}x\d+$/;

                      if (twoDigitPattern.test(value)) {
                        const regex = new RegExp(`(^|\\b)${value}(\\b|$)`);
                        return regex.test(metafield.value);
                      }

                      return metafield.value.includes(value);
                    });
                  }
          
                  return true;
                });
          
                if (!isProductMatch) return null;
          
                const matchingVariants = (product.variants?.edges || []).filter(({ node }) => {
                  const variantMetafields = node.metafields || [];
          
                  const allAnswersMatch = selectedAnswers.every(answer => {
                    if (answer.searchVariant) {
                      const variantField = variantMetafields.find(metafield =>
                        metafield?.namespace === answer.namespace && metafield?.key === answer.key
                      );
          
                      if (answer.compatibleValues.length === 1 && answer.compatibleValues[0] === "false") {
                        return !variantField || variantField.value === "false";
                      }
          
                      if (!variantField) return false;
          
                      return answer.compatibleValues.some(value => variantField.value.includes(value));
                    }
          
                    return true;
                  });
          
                  const isWithinBudget = this.getIsWithinBudget(node);
          
                  return allAnswersMatch && isWithinBudget;
                });
          
                if (matchingVariants.length === 0) return null;
          
                return {
                  ...product,
                  variants: {
                    edges: matchingVariants
                  }
                };
              })
              .filter(product => product !== null);
          }
          
          const filteredProducts = filterMatchingProducts.call(this, products, selectedAnswers);
          return filteredProducts;
        }

        matchesFasthetsField(required, value) {
          // Normalize both sides of the metafield value
          const parts = value.split('/').map(p => p.trim());
        
          // Case: only one required value
          if (required.length === 1) {
            return parts.some(p => p === required[0]);
          }
        
          // Case: two required values
          if (required.length === 2) {
            // Must match both, including count
            const requiredSorted = [...required].sort();
            const partsSorted = [...parts].sort();
        
            // Check exact match of sorted arrays
            return (
              requiredSorted.length === partsSorted.length &&
              requiredSorted.every((val, index) => val === partsSorted[index])
            );
          }
        
          return false;
        }

        getUsersInformation() {
          const userInformationEls = this.querySelectorAll('[data-is-users]');
          const quizBudgetEl = this.querySelector('[data-is-budget]');
          const userInfoProp = [];
          userInformationEls.forEach(el => {
            if (!userInfoProp.includes(el.name)) {
              userInfoProp.push(el.name);
              this.data.userInformation[el.name] = el.value;
            }
          });
          if (quizBudgetEl) {
            this.data.userInformation.budget = quizBudgetEl.value;
          }
        }

        async fetchAllProductsInCollection(collectionHandle) {
          const {productMetafieldIdentifiers, variantMetafieldIdentifiers} = this.getMetafieldIdentifiers();
          const url = `https://${Shopify.shop}/api/2024-04/graphql.json`;
          const storefrontAccessToken = this.dataset.storefrontAccessToken;
          if (!storefrontAccessToken) {
            console.log('no storefront access token to fetch result products');
            return;
          }

          const query = `
            query getProductsWithDynamicMetafields(
              $collectionHandle: String!,
              $metafieldIdentifiers: [HasMetafieldsIdentifier!]!,
              $variantMetafieldIdentifiers: [HasMetafieldsIdentifier!]!,
              $first: Int!,
              $after: String
            ) {
              collection(handle: $collectionHandle) {
                products(first: $first, after: $after) {
                  pageInfo {
                    hasNextPage
                    endCursor
                  }
                  edges {
                    node {
                      id
                      title
                      handle
                      description
                      metafields(identifiers: $metafieldIdentifiers) {
                        namespace
                        key
                        value
                      }
                      variants (first: 50) {
                        edges {
                          node {
                            id
                            title
                            price {
                              amount
                              currencyCode
                            }
                            metafields(identifiers: $variantMetafieldIdentifiers) {
                              namespace
                              key
                              value
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `;

          let allProducts = [];
          let hasNextPage = true;
          let cursor = null;

          while (hasNextPage) {
            const variables = {
              collectionHandle: collectionHandle,
              first: 250, 
              after: cursor, 
              metafieldIdentifiers: productMetafieldIdentifiers,
              variantMetafieldIdentifiers: variantMetafieldIdentifiers
            };
    
            try {
              const response = await axios.post(url, { query, variables }, {
                headers: {
                  'Content-Type': 'application/json',
                  'X-Shopify-Storefront-Access-Token': storefrontAccessToken
                }
              });
  
              const data = response.data;
              console.log('resp data', data);
  
              if (data.errors) {
                console.error("GraphQL Errors:", data.errors);
                break; 
              }
  
              const productsData = data.data.collection.products;
              const newProducts = productsData.edges.map(edge => edge.node);
              
              allProducts = allProducts.concat(newProducts);
  
              hasNextPage = productsData.pageInfo.hasNextPage;
              cursor = productsData.pageInfo.endCursor;
    
            } catch (error) {
              console.error("An API error occurred during pagination:", error.message);
              if (error.response) {
                console.error("Response Data:", error.response.data);
              }
              break; // Exit loop on failure
            }
          }
    
          return allProducts;
        }

        getMetafieldIdentifiers() {
          const metafieldHandleElements = this.querySelectorAll('[data-metafield-handle] .quiz-answer input', '.quiz-question .quiz-answer [data-metafield-handle], .quiz-question .quiz-answer[data-metafield-handle]');
          const productMetafieldIdentifiers = [];
          const variantMetafieldIdentifiers = [];
          const addedIdentifiers = {};

          metafieldHandleElements.forEach(metafieldHandleElement => {
            const metafieldHandle = metafieldHandleElement.dataset?.metafieldHandle || metafieldHandleElement.closest('[data-metafield-handle]').dataset.metafieldHandle;
            if (!metafieldHandle) return;
            if (addedIdentifiers[metafieldHandle]) return;
            const [namespace, key] = metafieldHandle.split('.');
            const isMetafieldMissing = metafieldHandle.includes('undefined');

            if (metafieldHandle.includes('variant:')) {
              variantMetafieldIdentifiers.push({
                namespace: namespace.split(':')[1],
                key,
              });
            } else if (!isMetafieldMissing) {
              productMetafieldIdentifiers.push({
                namespace,
                key,
              });
            }

            if (!isMetafieldMissing) addedIdentifiers[metafieldHandle] = true;
          });

          return {productMetafieldIdentifiers, variantMetafieldIdentifiers};
        }

        decodeHtmlEntities(text) {
          const textArea = document.createElement('textarea');
          textArea.innerHTML = text;
          return textArea.value;
      }

      getIsWithinBudget(variant) {
        return this.data.userInformation.budget ?
          parseFloat(variant.price?.amount || 0) <= parseFloat(this.data.userInformation.budget) :
          true;
      }
    }

  customElements.define('custom-quiz', Quiz);
}